/*Copyright (c) 2023. Honeywell International, Inc. All rights reserved.
This file contains trade secrets of Honeywell International, Inc. 
No part may be reproduced or transmitted in any form by any means or for anypurpose without the express written permission of Honeywell. */
import { useEffect, useRef, useState } from "react";
import { Button, Paper, Typography, Grid } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../../utils/axios-instance/AxiosInstance";
import { ConfigurationVariables } from "../../config";
import TextInputField from "../../utils/text-input-field/TextInputField";
import AcceptTerms from "../../utils/accept-terms/AcceptTerms";
import "./CreateUser.scss";
import classes from "./CreateUser.module.css";
import InfoModal from "../../utils/modals/info-modal/InfoModal";
import Loader from "../Loader/Loader";
import UploadProfilePic from "../../utils/upload-profile-pic/UploadProfilePic";
import AutoCompleteMultiSelect from "../../utils/multiselect-dropdown/AutoCompleteMultiSelect";
import { IErrorProps } from "../../models";
import DialogForError from "../SharedComponents/DialogForError";
import ToastBarForError from "../SharedComponents/ToastBarForError";
import getErrorForUserPages from "../../utils/methods/getErrorForUserPages";
import { getAccessToken } from "../../utils";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { userActions } from "../../store/User/user-slice";
import { getRolesWithTypeProp } from "../../utils/methods/roles";
import editLogo from "./EditLogo.png";

function EditUser() {
  const navigate = useNavigate();
  const location = useLocation();
  const { UserInfo, CustomerId } = location?.state?.userDetailsToEdit;

  const [isFormDataChanged, setIsFormDataChanged] = useState<boolean>(false);
  const [userId, setUserId] = useState<string>(UserInfo?.userId);
  const [existingProfilePic, setExistingProfilePic] = useState<string>();
  const [roleOptions, setRoleOptions] = useState<any>([]);
  const [firstName, setFirstName] = useState(UserInfo?.firstName);
  const [lastName, setLastName] = useState(UserInfo?.lastName);
  const [emailId, setEmailId] = useState(UserInfo?.emailId);
  const [roleId, setRoleIds] = useState<string[]>(
    UserInfo?.roles?.map((role: any) => role?.roleId)
  );
  const [defaultRoles, setDefaultRoles] = useState<any[]>(
    UserInfo?.roles.map((role: any) => {
      return {
        label: role?.name,
        value: role?.roleId,
        ...role,
      };
    })
  );
  const [userProfilePic, setUserProfilePic] = useState<any>();
  const [isRoleIdEmpty, setIsRoleIdEmpty] = useState<boolean>(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState<boolean>(false);
  const [isFormFilled, setIsFormFilled] = useState<boolean>(false);
  const [successModal, setSuccessModal] = useState<boolean>(false);
  const [errorModal, setErrorModal] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [currentCustomerID, setCurrentCustomerID] = useState<string | null>(
    CustomerId
  );
  const [errorList, setErrorList] = useState<Array<IErrorProps>>([]);
  const [openDialogNoURLError, setOpenDialogNoURLError] = useState(false);
  const [openDialogServerError, setOpenDialogServerError] = useState(false);
  const [openToastError, setOpenToastError] = useState(false);
  const { UserMgmt_Endpoints, Api, Version, UserController } =
    ConfigurationVariables;
  let usersUrl = `${UserMgmt_Endpoints}/${Api}/${Version}/${UserController}`;
  const rolesUrl = `${usersUrl}/roles`;
  let isUserUpdated = useRef({
    profilePicUpdate: false,
    rolesUpdate: false,
  });

  const headers = {
    customerId: currentCustomerID,
    accept: "application/json",
    Authorization: `Bearer ${getAccessToken()}`,
  };

  const dispatch: AppDispatch = useDispatch();
  /** Effect to set error dialog or error toastbar state to open */
  useEffect(() => {
    if (
      errorList.some((x) => x.errorCode === undefined) &&
      !openDialogNoURLError
    ) {
      setOpenDialogNoURLError(true);
    }
    if (errorList.some((x) => x.errorCode === 400) && !openToastError) {
      setOpenToastError(true);
    }
    if (
      errorList.some(
        (x) =>
          x.errorCode !== undefined &&
          x.errorCode !== 400 &&
          x.errorCode !== null &&
          x.errorCode !== 204
      ) &&
      !openDialogServerError
    ) {
      setOpenDialogServerError(true);
    }
  }, [errorList]);

  /**To get the user profile pic and show the preview */
  useEffect(() => {
    setExistingProfilePic(editLogo);
    let uploadProfilePicUrl = `${usersUrl}/userpic/${userId}`;
    setLoading(true);
    axios
      .get(uploadProfilePicUrl, {
        headers: {
          ...headers,
        },
      })
      .then((response) => {
        if (response.status !== 204) {
          setExistingProfilePic(response.data);
        } else {
          setExistingProfilePic(editLogo);
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setErrorModal(true);
        setErrorMsg(error?.message);

        // Exclude throwing error if profile pic is not found for a valid user
        if (error.response.status !== 204) {
          let newError: IErrorProps = getErrorForUserPages(error);
          setErrorList((prevState) => [...prevState, newError]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  /**To get roles options for the assign roles dropdown*/
  useEffect(() => {
    const headers = {
      customerId: currentCustomerID,
      accept: "application/json",
      Authorization: `Bearer ${getAccessToken()}`,
    };
    setLoading(true);
    axios
      .get(rolesUrl, {
        headers: {
          ...headers,
        },
      })
      .then((response) => {
        setIsRoleIdEmpty(false);
        setRoleOptions(
          response.data.map((obj: any) => {
            return {
              label: obj.name,
              value: obj.roleId,
              ...obj,
            };
          })
        );
      })
      .catch((error) => {
        let newError: IErrorProps = getErrorForUserPages(error);

        setErrorList((prevState) => [...prevState, newError]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  /**
   *
   * @param event
   * @param values selected values - list of objects
   * @param reason
   *
   * sets the selected options in the state
   */
  const assignRolesHandler = (
    event: React.ChangeEvent<{}>,
    values: any[],
    reason: string
  ) => {
    const selectedRoleValues = values && values.map((obj) => obj.value);

    setRoleIds(selectedRoleValues);
    selectedRoleValues?.length > 0 && setIsRoleIdEmpty(false);

    /**Checking whether form data changed or not */
    let defaultRoleIds = defaultRoles.map((obj: any) => obj.roleId);
    if (
      defaultRoleIds.length === selectedRoleValues.length &&
      defaultRoleIds.every((el) => selectedRoleValues.includes(el))
    ) {
      /**Form data not changed */
      isUserUpdated.current = { ...isUserUpdated.current, rolesUpdate: false };
    } else {
      isUserUpdated.current = { ...isUserUpdated.current, rolesUpdate: true };
    }
    handleEnableOrDisableUpdateButton();
  };

  // Enable or disable user update button
  const handleEnableOrDisableUpdateButton = () => {
    const isAnyValueTrue = Object.values(isUserUpdated.current).some(
      (value) => value
    );

    if (isAnyValueTrue) {
      setIsFormDataChanged(true);
    } else {
      setIsFormDataChanged(false);
    }
  };

  const cancelBtnHandler = () => {
    navigate(-1);
  };

  /**
   * @summery
   * input fields validation: empty field check
   * email basic validation
   * opens confirm modal if all mandatory fields entered
   */
  const updateBtnHandler = () => {
    setIsFormFilled(true);
    !roleId && setIsRoleIdEmpty(true);
    roleId?.length == 0 && setIsRoleIdEmpty(true);

    if (roleId && !isTermsAccepted) {
      setIsTermsAccepted(false);
    }

    // checking if roles are selected and updating roles and profile pic (if changed)
    if (roleId && roleId.length > 0 && isTermsAccepted) {
      setErrorModal(false);

      updateUserRoles();
      userProfilePic && submitUploadedProfilePic(); //only if user profile changed
    } else {
    }
  };

  /**
   *
   * Updating selected user roles
   */
  const updateUserRoles = () => {
    let reqBody = {
      roles: getRolesWithTypeProp(defaultRoles, roleId),
    };

    setLoading(true);
    axios
      .patch(`${usersUrl}/${userId}`, reqBody, {
        headers: {
          ...headers,
        },
      })
      .then((response) => {
        setLoading(false);
        setSuccessModal(true);
        // need to dispatch after editing a Honeywell user
        // No info of updated User comes from API , so using the roleIds of request API
        // to update the new roles for the user
        // Pass the userId and roleIds array to reducer.
        dispatch(
          userActions.updateUserRoles({
            userId: userId,
            roleIds: roleId,
          })
        );
      })
      .catch((error) => {
        setLoading(false);
        setErrorModal(true);
        setErrorMsg(error?.message);

        let newError: IErrorProps = getErrorForUserPages(error);

        setErrorList((prevState) => [...prevState, newError]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /**
   *
   * For submitting uploaded profile pic to save in database
   */
  const submitUploadedProfilePic = () => {
    let uploadProfilePicUrl = `${usersUrl}/userpic/${userId}`;
    let formData = new FormData();
    formData.append("logo", userProfilePic);
    if (userProfilePic) {
      setLoading(true);
      axios
        .put(uploadProfilePicUrl, formData, {
          headers: {
            ...headers,
          },
        })
        .then((response) => {
          setLoading(false);
          setSuccessModal(true);
        })
        .catch((error) => {
          setLoading(false);
          setErrorModal(true);
          setErrorMsg(error?.message);
          //
          let newError: IErrorProps = getErrorForUserPages(error);

          setErrorList((prevState) => [...prevState, newError]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };
  // Error handling handlers
  const onDialogForServerCloseHandler = () => setOpenDialogServerError(false);
  const onDialogForNOURLCloseHandler = () => setOpenDialogNoURLError(false);

  const handleToastBarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenToastError(false);
  };
  // End error handling handlers
  return (
    <div className="create-user-dashboard">
      <Paper className="paper">
        <Grid container direction="row" p={1} className="grid-Heading">
          <Grid
            item
            xs={12}
            sx={{ paddingBottom: 1, display: "flex", flexDirection: "row" }}
          >
            <Button
              onClick={() => {
                navigate(-1);
              }}
              sx={{
                color: "#F0F0F0",
                padding: "0vh 0vh 0.7vh 0vh",
                fontSize: "1.1 rem",
                justifyContent: "start",
                minWidth: "1vw",
                minHeight: "0.5vh",
              }}
            >
              <ArrowBackIosIcon
                data-testid="arrow-back-icon"
                sx={{ fontSize: "0.9 rem" }}
              />
            </Button>
            <Typography className="mainheading">Edit User</Typography>
          </Grid>

          <Grid
            container
            direction="column"
            sx={{
              height: "30vh",
              backgroundColor: "#272727",
              minWidth: "800px",
              minHeight: "200px",
              width: "99%",
              border: "1px solid #101010",
              boxShadow:
                "1px 1px 1px rgba(0, 0, 0, 0.15), 1px 1px 1px rgba(0, 0, 0, 0.15)",
            }}
          >
            <Grid
              item
              sx={{
                height: "20%",
                background: "#272727",
              }}
            >
              <Typography className={classes.secondaryHeading}>
                PROFILE INFORMATION
              </Typography>
            </Grid>
            {/*  */}
            <Grid
              item
              sx={{
                height: "80%",
                //  backgroundColor: "green"
              }}
            >
              <Grid
                container
                direction="row"
                sx={{
                  width: "100%",
                  height: "100%",
                }}
              >
                <Grid
                  item
                  sx={{
                    width: "18%",
                    minHeight: "120px",
                    minWidth: "120px",
                  }}
                >
                  {existingProfilePic === editLogo ? (
                    <UploadProfilePic
                      profilePic={existingProfilePic}
                      onImageUpload={(image) => {
                        setUserProfilePic(image);

                        isUserUpdated.current = {
                          ...isUserUpdated.current,
                          profilePicUpdate: true,
                        };

                        handleEnableOrDisableUpdateButton();
                      }}
                      isUploadDisabled={false}
                    />
                  ) : (
                    <UploadProfilePic
                      profilePic={`data:image/jpeg;base64,${existingProfilePic}`}
                      onImageUpload={(image) => {
                        setUserProfilePic(image);

                        isUserUpdated.current = {
                          ...isUserUpdated.current,
                          profilePicUpdate: true,
                        };

                        handleEnableOrDisableUpdateButton();
                      }}
                      isUploadDisabled={false}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  sx={{
                    // backgroundColor: "black",
                    width: "80%",
                    display: "flex",
                  }}
                >
                  <Grid
                    sx={{
                      width: "50%",
                      // backgroundColor: "aqua",
                      padding: "1%",
                    }}
                  >
                    <TextInputField
                      fieldLabel="First Name"
                      disabled
                      id="FirstName"
                      type="text"
                      value={firstName}
                      required={true}
                      dataTestId="firstName"
                      styleName="edit-text-input-field"
                    />
                    <TextInputField
                      disabled
                      fieldLabel="Email ID"
                      id="EmailID"
                      type="text"
                      value={emailId}
                      required={true}
                      dataTestId="emailId"
                      styleName="edit-text-input-field"
                    />
                  </Grid>
                  <Grid
                    sx={{
                      width: "50%",
                      padding: "1%",
                    }}
                  >
                    <TextInputField
                      disabled
                      fieldLabel="Last Name"
                      id="LastName"
                      type="text"
                      value={lastName}
                      required={true}
                      dataTestId="lastName"
                      styleName="edit-text-input-field"
                    />
                    <AutoCompleteMultiSelect
                      label="Assign Roles"
                      required
                      options={roleOptions}
                      onChange={assignRolesHandler}
                      hasError={isRoleIdEmpty}
                      helperText={isRoleIdEmpty ? "Please select roles" : ""}
                      defaultValues={defaultRoles}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {/* Accept terms and conditions checkbox with text********************** */}
          <AcceptTerms
            isFormFilled={isFormFilled}
            isTermsAccepted={isTermsAccepted}
            onTermsAccept={(event) => {
              setIsTermsAccepted(event?.target.checked);
            }}
          />
          {/*  */}
        </Grid>
      </Paper>
      {/* cancel and create buttons************************************************** */}

      <Grid
        item
        xs={12}
        sx={{
          backgroundColor: "#272727",
          width: "98%",
          border: "1px solid #101010",
          boxShadow:
            "1px 1px 1px rgba(0, 0, 0, 0.15), 1px 1px 1px rgba(0, 0, 0, 0.15)",
          // marginTop: ".1vh",
          display: "flex",
          position: "fixed",
          bottom: 1,
          height: "8vh",
        }}
      >
        <Grid sx={{ height: "3vh", margin: "0.5vh" }}>
          <Button
            color="primary"
            fullWidth
            sx={{
              height: "1vh",
              margin: "1vw",
              position: "relative",
            }}
            className="cancelButton"
            onClick={() => cancelBtnHandler()}
          >
            Cancel
          </Button>
        </Grid>
        <Grid
          container
          justifyContent="flex-end"
          sx={{ height: "3vh", paddingRight: "1.5vw", margin: "0.5vh" }}
        >
          <Button
            sx={{
              fontSize: 12,
              padding: 2,
              height: "1vh",
              position: "relative",
              margin: "0.5vw",
              opacity: !isFormDataChanged ? "0.4" : "1",
            }}
            className="createButton"
            variant="contained"
            onClick={updateBtnHandler}
            disabled={!isFormDataChanged}
          >
            Update{" "}
          </Button>
        </Grid>
      </Grid>
      <InfoModal
        dialogTitle="Successfully Updated"
        dialogContentText="User Account has been updated."
        onOkClick={() => {
          setSuccessModal(false);
          navigate(-1);
        }}
        open={successModal}
      />
      {/* /// Error handling logic */}
      {loading && <Loader />}
      {!loading && openDialogServerError && (
        <DialogForError
          open={openDialogServerError}
          onClose={onDialogForServerCloseHandler}
          errorList={errorList}
          errorType="serverError"
        />
      )}
      {/* /// Open NoURL error */}
      {!loading && openDialogNoURLError && (
        <DialogForError
          open={openDialogNoURLError}
          onClose={onDialogForNOURLCloseHandler}
          errorList={errorList}
          errorType="noURLFoundError"
        />
      )}
      {/* /// Open Toast error */}
      {!loading && openToastError && (
        <ToastBarForError
          open={openToastError}
          onClose={handleToastBarClose}
          errorList={errorList}
        />
      )}
      {/* /// End error handling logic */}
    </div>
  );
}

export default EditUser;
